import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, ArrowRight, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Sign in — Admin · A.R.U.";
    if (user && user !== false) {
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="admin-login-root"
      className="relative min-h-screen bg-black text-white flex items-center justify-center px-6"
    >
      <div className="absolute inset-x-0 top-0 h-[60vh] radial-glow pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md"
      >
        <Link
          to="/"
          data-testid="login-back-home"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white text-sm transition-colors mb-10"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
          Back to portfolio
        </Link>

        <div className="text-sm text-zinc-500">Admin · Sign in</div>
        <h1 className="font-heading mt-3 text-4xl md:text-5xl font-light tracking-tight text-white">
          Welcome back.
        </h1>
        <p className="mt-3 text-sm text-zinc-400 font-light">
          Manage your selected works and trajectory.
        </p>

        <form
          onSubmit={submit}
          className="mt-10 space-y-5 bg-white/[0.02] border border-white/10 rounded-2xl p-7"
        >
          <div>
            <label className="block text-sm text-zinc-400 mb-2">Email</label>
            <div className="flex items-center gap-3 bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus-within:border-white/30 transition-colors">
              <Mail className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
              <input
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-transparent outline-none flex-1 text-white placeholder:text-zinc-600"
                placeholder="you@domain.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-zinc-400 mb-2">Password</label>
            <div className="flex items-center gap-3 bg-black/40 border border-white/10 rounded-xl px-4 py-3 focus-within:border-white/30 transition-colors">
              <Lock className="w-4 h-4 text-zinc-500" strokeWidth={1.5} />
              <input
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-transparent outline-none flex-1 text-white placeholder:text-zinc-600"
                placeholder="••••••••"
              />
            </div>
          </div>

          {error && (
            <div
              data-testid="login-error"
              className="text-sm text-red-300 bg-red-500/5 border border-red-500/20 rounded-xl px-4 py-3"
            >
              {error}
            </div>
          )}

          <button
            data-testid="login-submit"
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-white text-black px-5 py-3 rounded-full text-sm font-medium hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100 transition-transform"
          >
            {submitting ? "Signing in…" : "Sign in"}
            <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </button>
        </form>
      </motion.div>
    </main>
  );
}
